import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BrainCircuit,
  Clock3,
  ExternalLink,
  Gauge,
  FileSearch,
  RefreshCw,
  ScanSearch,
  ShieldAlert,
  Sparkles,
  Target,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { DashboardMarketData, FundDashboardData, FundDashboardItem } from "../../shared/types";
import { AiOutput, Badge, Button, Card, SectionHeader, Spinner } from "../components/Ui";
import { api } from "../lib/api";
import { useData } from "../lib/data";

function signed(value: number, digits = 2) {
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

function marketTone(value: number) {
  return value > 0 ? "market-up" : value < 0 ? "market-down" : "market-flat";
}

function formatTurnover(value: number) {
  if (value >= 10_000) return `${(value / 10_000).toFixed(2)}万亿`;
  return `${Math.round(value).toLocaleString("zh-CN")}亿`;
}

function formatAsOf(value: string) {
  if (!value) return "等待行情";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatNewsTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function MarketLineChart({ data, preClose }: { data: DashboardMarketData["intraday"]; preClose: number }) {
  if (data.length < 2) return <div className="dashboard-chart-empty"><BarChart3 size={22} /><span>分时曲线等待数据源恢复</span></div>;
  const width = 680;
  const height = 210;
  const values = [...data.map((item) => item.value), preClose].filter((value) => value > 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.16, max * 0.002);
  const low = min - padding;
  const high = max + padding;
  const y = (value: number) => 16 + (high - value) / Math.max(high - low, 1) * (height - 42);
  const x = (index: number) => index / Math.max(data.length - 1, 1) * width;
  const path = data.map((item, index) => `${index ? "L" : "M"}${x(index).toFixed(1)},${y(item.value).toFixed(1)}`).join(" ");
  const areaPath = `${path} L${width},${height - 24} L0,${height - 24} Z`;
  const isUp = data[data.length - 1].value >= preClose;
  const tone = isUp ? "#c64a3d" : "#2f7a5e";
  return (
    <div className="dashboard-market-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-label="上证指数分时走势">
        <defs>
          <linearGradient id="dashboardArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={tone} stopOpacity=".28" />
            <stop offset="1" stopColor={tone} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.2, 0.4, 0.6, 0.8].map((ratio) => <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="chart-grid-line" />)}
        <line x1="0" x2={width} y1={y(preClose)} y2={y(preClose)} className="chart-preclose-line" />
        <path d={areaPath} fill="url(#dashboardArea)" />
        <path d={path} fill="none" stroke={tone} strokeWidth="2.4" vectorEffect="non-scaling-stroke" />
        <circle cx={width} cy={y(data[data.length - 1].value)} r="4" fill={tone} />
      </svg>
      <div className="dashboard-chart-axis"><span>{data[0].time.slice(11, 16)}</span><span>昨收 {preClose.toFixed(2)}</span><span>{data[data.length - 1].time.slice(11, 16)}</span></div>
    </div>
  );
}

function FundMiniTrend({ fund }: { fund: FundDashboardItem }) {
  const values = fund.trend.map((item) => item.value);
  if (values.length < 2) return <span className="fund-mini-empty">—</span>;
  const width = 150; const height = 44; const min = Math.min(...values); const max = Math.max(...values);
  const path = values.map((value,index)=>`${index?"L":"M"}${(index/(values.length-1)*width).toFixed(1)},${(4+(max-value)/Math.max(max-min,.001)*(height-8)).toFixed(1)}`).join(" ");
  return <svg className="dashboard-fund-spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"><path d={path} fill="none" stroke={fund.yearReturn>=0?"#b64036":"#2e7358"} strokeWidth="2" vectorEffect="non-scaling-stroke"/></svg>;
}

function DashboardLoading() {
  return <Card className="dashboard-loading"><Spinner label="正在同步基金净值、经理档案与市场背景" /><p>基金数据与 A 股行情分别读取，任一数据源异常都不会阻断另一侧。</p></Card>;
}

export function DashboardPage() {
  const { data: workspace, refresh: refreshWorkspace } = useData();
  const [market, setMarket] = useState<DashboardMarketData | null>(null);
  const [funds, setFunds] = useState<FundDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [brief, setBrief] = useState("");
  const [briefModel, setBriefModel] = useState("");
  const [briefBusy, setBriefBusy] = useState(false);
  const [briefSaved, setBriefSaved] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const [marketResult, fundResult] = await Promise.allSettled([api.dashboardMarket(refresh), api.dashboardFunds(refresh)]);
      if (marketResult.status === "fulfilled") setMarket(marketResult.value);
      if (fundResult.status === "fulfilled") setFunds(fundResult.value);
      if (marketResult.status === "rejected" && fundResult.status === "rejected") throw marketResult.reason;
      if (fundResult.status === "rejected") setError(`基金观察池暂时未完全同步：${fundResult.reason instanceof Error ? fundResult.reason.message : "公开数据异常"}`);
      else if (marketResult.status === "rejected") setError(`底层市场背景暂时未完全同步：${marketResult.reason instanceof Error ? marketResult.reason.message : "公开数据异常"}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "市场数据加载失败");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const heatmap = useMemo(() => [...(market?.sectors ?? [])].sort((a, b) => b.turnoverYi - a.turnoverYi).slice(0, 12), [market]);
  const primaryIndex = market?.indices[0];
  const totalBreadth = (market?.breadth.advancers ?? 0) + (market?.breadth.decliners ?? 0) + (market?.breadth.flat ?? 0);

  const generateBrief = async () => {
    if (!market && !funds) return;
    setBriefBusy(true); setError(""); setBriefSaved(false);
    try {
      const result = await api.research("dashboard", `为${workspace?.profile.portfolioName || "当前组合"}生成今日晨会提纲。`, {
        asOf: funds?.asOf || market?.marketAsOf,
        profile: workspace?.profile,
        template: workspace?.templates.find((item) => item.workflow === "dashboard" && item.isDefault),
        governedSources: workspace?.sources.filter((item) => item.status === "connected"),
        representativeFunds: funds?.funds,
        fundStyles: funds?.styleSummary,
        indices: market?.indices,
        pulse: market?.pulse,
        breadth: market?.breadth,
        sectors: heatmap,
        news: market?.news.map((item) => ({ title: item.title, category: item.category, summary: item.summary, relatedStocks: item.relatedStocks })),
        pendingReviews: workspace?.memories.filter((item) => item.status === "待验证").slice(0, 8),
      });
      setBrief(result.content); setBriefModel(result.model);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "晨会提纲生成失败"); }
    finally { setBriefBusy(false); }
  };

  const saveBrief = async () => {
    if (!brief) return;
    await api.saveMemory({ title: `晨会提纲 · ${new Date().toISOString().slice(0, 10)}`, thesis: brief.slice(0, 480), evidence: `${market?.news.length || 0}条关键新闻、${market?.indices.length || 0}个指数和市场宽度`, decisionType: "市场观察", reviewDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), tags: ["晨会", "市场脉冲"], source: "投研驾驶舱 · DeepSeek", status: "待验证", confidence: 60 });
    await refreshWorkspace();
    setBriefSaved(true);
  };

  return (
    <div className="page-stack dashboard-v2">
      <div className="page-title dashboard-title">
        <div className="dashboard-title-copy">
          <span className="eyebrow">WYX FUND INTELLIGENCE</span>
          <h1>投研驾驶舱</h1>
          <p>面向机构客户的基金观察工作台&nbsp;&nbsp;先看基金风格 净值表现与经理变化&nbsp;&nbsp;再从指数 板块与新闻理解底层市场</p>
        </div>
        <div className="dashboard-title-actions">
          <div className="dashboard-asof"><span>{funds ? "基金数据已更新" : market?.sessionLabel ?? "正在同步"}</span><strong>{funds?.asOf || (market ? formatAsOf(market.marketAsOf) : "—")}</strong><small>基金净值&nbsp;&nbsp;市场背景&nbsp;&nbsp;红涨绿跌</small></div>
          <Button className="secondary" onClick={() => void load(true)} disabled={refreshing}><RefreshCw size={14} className={refreshing ? "spin-icon" : ""} />{refreshing ? "刷新中" : "刷新"}</Button>
          <Button onClick={() => void generateBrief()} disabled={(!market&&!funds) || briefBusy}>{briefBusy ? <Spinner label="生成晨会"/> : <><BrainCircuit size={14}/>生成基金晨会</>}</Button>
        </div>
      </div>

      <nav className="dashboard-research-rail" aria-label="基金研究工作流">
        <Link to="/fund-research"><span>01</span><ScanSearch size={17}/><div><strong>基金尽调</strong><small>净值 经理 风格 持仓 持有人</small></div><ArrowRight size={14}/></Link>
        <Link to="/one-pager"><span>02</span><Target size={17}/><div><strong>底层穿透</strong><small>A股重仓联动公司与行业研究</small></div><ArrowRight size={14}/></Link>
        <Link to="/committee"><span>03</span><BrainCircuit size={17}/><div><strong>基金投委会</strong><small>事实账本 交叉质询 人工决议</small></div><ArrowRight size={14}/></Link>
        <Link to="/materials"><span>04</span><FileSearch size={17}/><div><strong>内部资料</strong><small>路演 调研与产品材料授权入口</small></div><ArrowRight size={14}/></Link>
      </nav>

      {loading && <DashboardLoading />}
      {error && <Card className="dashboard-error"><ShieldAlert size={18} /><div><strong>{market||funds?"部分公开数据暂未同步":"暂时无法同步公开数据"}</strong><span>{error}</span></div><Button className="secondary" onClick={() => void load()}>重新加载</Button></Card>}

      {funds && <section className="dashboard-fund-overview">
        <Card className="dashboard-fund-lead">
          <div className="dashboard-fund-lead-head"><div><span>FUND RESEARCH WATCHLIST</span><h2>基金研究观察池</h2><p>覆盖主动权益 指数 固收 现金管理与海外策略&nbsp;&nbsp;统一观察净值 风格 经理与规模变化</p></div><Link to="/fund-research"><ScanSearch size={15}/>进入基金一页纸<ArrowRight size={14}/></Link></div>
          <div className="dashboard-fund-table"><div className="dashboard-fund-table-head"><span>基金 / 管理人</span><span>近60期净值</span><span>单位净值</span><span>近1月</span><span>近1年</span><span>规模</span></div>{funds.funds.map((fund)=><Link key={fund.code} to={`/fund-research?q=${fund.code}`}><span className="dashboard-fund-name"><strong>{fund.name}</strong><small>{fund.code} · {fund.manager||fund.company}</small></span><FundMiniTrend fund={fund}/><b className="dashboard-fund-nav">{fund.nav.toFixed(4)}<small>{fund.navDate}</small></b><b className={`dashboard-fund-return month ${marketTone(fund.monthReturn)}`}>{signed(fund.monthReturn)}%</b><b className={`dashboard-fund-return year ${marketTone(fund.yearReturn)}`}>{signed(fund.yearReturn)}%</b><b className="dashboard-fund-scale">{fund.scaleYi > 0 ? `${fund.scaleYi.toFixed(1)}亿` : "—"}</b></Link>)}</div>
        </Card>
        <aside className="dashboard-fund-side">
          <Card className="dashboard-style-card"><SectionHeader title="策略表现分布" subtitle="观察池近一年简单平均 用于快速识别策略分化" action={<Target size={17}/>}/>{funds.styleSummary.map((style)=><div key={style.label}><span><strong>{style.label}</strong><small>{style.count}只样本</small></span><i><b style={{width:`${Math.min(100,Math.max(8,Math.abs(style.averageReturn)*3))}%`}} className={`tone-bg-${style.tone}`}/></i><strong className={marketTone(style.averageReturn)}>{signed(style.averageReturn)}%</strong></div>)}</Card>
          <Card className="dashboard-fund-entry"><span>旗舰研究入口</span><h2>从基金到持仓的完整研究底稿</h2><p>汇总净值 经理 风格 重仓 规模与持有人结构&nbsp;&nbsp;A股重仓可继续下钻公司和行业</p><Link to="/fund-research"><Sparkles size={15}/>开始研究基金<ArrowRight size={14}/></Link><Link to="/materials"><FileSearch size={15}/>导入路演与调研资料<ArrowRight size={14}/></Link></Card>
        </aside>
      </section>}

      {market && (
        <>
          <div className="dashboard-market-divider"><span>MARKET CONTEXT</span><h2>底层市场背景</h2><p>指数、板块和新闻仅作为基金表现解释层，避免把首页重新变成股票行情屏。</p></div>
          <div className="dashboard-index-strip">
            {market.indices.map((index) => (
              <article key={index.code}>
                <div><span>{index.name}</span><small>{index.code}</small></div>
                <strong>{index.value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                <b className={marketTone(index.changePercent)}>{signed(index.changePercent)}%</b>
                <small>振幅 {index.amplitude.toFixed(2)}% · {formatTurnover(index.turnoverYi)}</small>
              </article>
            ))}
          </div>

          {brief && <Card className="dashboard-ai-brief"><SectionHeader title="AI 晨会提纲" subtitle={`基于当前市场截面、机构政策与待复盘记忆 · ${briefModel}`} action={<Button className="secondary small" onClick={() => void saveBrief()} disabled={briefSaved}>{briefSaved ? "已加入复盘队列" : "保存为投资记忆"}</Button>}/><AiOutput content={brief} model={briefModel}/></Card>}

          <div className="dashboard-workbench">
            <main className="dashboard-news-column">
              <Card className="dashboard-news-card">
                <SectionHeader
                  title="今日关键财经新闻"
                  subtitle="按市场影响、政策权重与资产关联度筛选；不是简单的热搜榜。"
                  action={<span className="dashboard-live-tag"><i /> DAILY CURATION</span>}
                />
                <div className="dashboard-news-list">
                  {market.news.slice(0, 4).map((item, index) => (
                    <article key={item.id}>
                      <div className="dashboard-news-rank">{String(index + 1).padStart(2, "0")}</div>
                      <div className="dashboard-news-body">
                        <div className="dashboard-news-meta"><span>{item.category}</span><time><Clock3 size={11} />{formatNewsTime(item.publishedAt)} · {item.source}</time><b>{"●".repeat(item.importance)}<i>{"●".repeat(5 - item.importance)}</i></b></div>
                        <a href={item.url} target="_blank" rel="noreferrer"><h2>{item.title}</h2><ExternalLink size={14} /></a>
                        <p>{item.summary}</p>
                        {item.relatedStocks.length > 0 && (
                          <div className="dashboard-related-stocks">
                            <span>关联 A 股</span>
                            {item.relatedStocks.map((stock) => (
                              <a key={stock.code} href={stock.url} target="_blank" rel="noreferrer">
                                <strong>{stock.name}</strong><small>{stock.code}</small><b className={marketTone(stock.changePercent)}>{signed(stock.changePercent)}%</b>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                  {!market.news.length && <div className="dashboard-empty-block">新闻数据源暂时没有返回可用条目。</div>}
                </div>
              </Card>
            </main>

            <aside className="dashboard-market-column">
              <Card className="dashboard-pulse-card">
                <div className="dashboard-pulse-head"><div><span>MARKET PULSE</span><strong>上证指数 · 分时</strong></div><Badge tone={primaryIndex && primaryIndex.changePercent >= 0 ? "down" : "up"}>{market.pulse.volatilityLabel}波动</Badge></div>
                <div className="dashboard-pulse-value">
                  <strong>{primaryIndex?.value.toFixed(2) ?? "—"}</strong>
                  <span className={marketTone(primaryIndex?.changePercent ?? 0)}>{signed(primaryIndex?.changePercent ?? 0)}%</span>
                </div>
                <MarketLineChart data={market.intraday} preClose={market.preClose} />
                <div className="dashboard-pulse-metrics">
                  <div><span>两市成交</span><strong>{formatTurnover(market.pulse.turnoverYi)}</strong></div>
                  <div><span>上涨占比</span><strong>{market.pulse.advanceRatio.toFixed(1)}%</strong></div>
                  <div><span>中位涨跌</span><strong className={marketTone(market.breadth.medianChange)}>{signed(market.breadth.medianChange)}%</strong></div>
                </div>
              </Card>

              <Card className="dashboard-heatmap-card">
                <SectionHeader title="板块冷热图" subtitle="面积近似成交额，颜色表示当日涨跌幅。" action={<Activity size={17} />} />
                <div className="dashboard-sector-heatmap">
                  {heatmap.map((sector, index) => {
                    const magnitude = Math.min(0.96, 0.42 + Math.abs(sector.changePercent) / 8);
                    const background = sector.changePercent >= 0 ? `rgba(179,55,45,${magnitude})` : `rgba(39,112,83,${magnitude})`;
                    return <div key={sector.code} className={index < 2 ? "heat-lg" : index < 5 ? "heat-md" : ""} style={{ background }} title={`${sector.name} ${signed(sector.changePercent)}% · 成交 ${formatTurnover(sector.turnoverYi)}`}><strong>{sector.name}</strong><b>{signed(sector.changePercent)}%</b><small>{formatTurnover(sector.turnoverYi)}</small></div>;
                  })}
                </div>
                <div className="dashboard-heatmap-legend"><span>绿 · 下跌</span><i /><span>平</span><i /><span>红 · 上涨</span></div>
              </Card>

              <Card className="dashboard-breadth-card">
                <SectionHeader title="市场宽度" subtitle={totalBreadth > 500 ? `${totalBreadth.toLocaleString("zh-CN")} 只 A 股横截面` : `${totalBreadth.toLocaleString("zh-CN")} 只成交活跃 A 股样本`} action={<Gauge size={17} />} />
                <div className="dashboard-breadth-numbers"><div><strong className="market-up">{market.breadth.advancers}</strong><span>上涨</span></div><div><strong>{market.breadth.flat}</strong><span>平盘</span></div><div><strong className="market-down">{market.breadth.decliners}</strong><span>下跌</span></div></div>
                <div className="dashboard-breadth-bar"><span style={{ width: `${totalBreadth ? market.breadth.advancers / totalBreadth * 100 : 0}%` }} /><i style={{ width: `${totalBreadth ? market.breadth.flat / totalBreadth * 100 : 0}%` }} /><b /></div>
                <div className="dashboard-limit-row"><span>涨停 <b className="market-up">{market.breadth.limitUp}</b></span><span>跌停 <b className="market-down">{market.breadth.limitDown}</b></span><span>波动评分 <b>{market.pulse.volatilityScore}/100</b></span></div>
              </Card>
            </aside>
          </div>

          <Card className="dashboard-data-governance">
            <div><span>DATA GOVERNANCE</span><strong>数据口径与决策留痕</strong><small>公开数据用于研究辅助&nbsp;&nbsp;关键结论保留来源 时间与人工复核状态</small></div>
            <footer><span>决策记忆 {workspace?.memories.length ?? 0} 条</span><span>行情缓存 5 分钟</span>{market.sources.map((source) => <a key={source.name} href={source.url} target="_blank" rel="noreferrer">{source.name}<ExternalLink size={11} /></a>)}</footer>
          </Card>
        </>
      )}
    </div>
  );
}
